// pages/MerchantOnboarding.jsx
//
// Account Entry (Screen 1) for the progressive-onboarding model.
// See docs/onboarding-analysis/ONBOARDING_UX_REDESIGN.md §2 & §8.
//
// Goal: minimum dataset, < 60s. Collect only name, brand, username, mobile (no OTP),
// email and password here - everything else (address, KYC docs, GST, bank, wallet) is
// deferred to the Onboarding Hub. On success we create the account, auto-login and land
// the merchant on the Hub (/onboarding), not an empty shipments table.
//
// Phase A (this file): live username-availability check + full inline validation.
// Phase B (later): email OTP (send/verify) will gate the primary CTA - the email field
// is intentionally structured so that step can slot in without a redesign.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from 'framer-motion';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

import { restoreSession } from '../redux/slices/authSlice';
import {
  registerUser,
  authenticateUser,
  checkUsernameAvailable,
  initiateOnboarding,
  updateOnboardingDetails,
} from '../services/onboardingService';
import { saveMerchantSignupToSheet } from '../services/googleSheetsService';
import { clearClientCache } from '../utils/clearClientCache';

// ── Brand assets (already shipped in /public) ───────────────────────────────
const LOGO_WHITE = '/wayuvega_logo_white.png';
const LOGO_DARK = '/wayuvega_logo.png';
const HERO_ART = '/login_page.svg';

// ── Validation helpers ──────────────────────────────────────────────────────
const USERNAME_REGEX = /^[a-zA-Z0-9._]{4,30}$/;
const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

function scorePassword(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

const STRENGTH = [
  { label: 'Too weak', color: 'bg-red-500', text: 'text-red-600' },
  { label: 'Weak', color: 'bg-orange-500', text: 'text-orange-600' },
  { label: 'Fair', color: 'bg-amber-500', text: 'text-amber-600' },
  { label: 'Good', color: 'bg-lime-500', text: 'text-lime-600' },
  { label: 'Strong', color: 'bg-green-600', text: 'text-green-700' },
];

// ── Small inline icons (no icon-lib dependency) ─────────────────────────────
const Spinner = ({ className = 'w-4 h-4' }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);
const CheckIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
);
const XIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 01-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 01-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" clipRule="evenodd" /></svg>
);

// Check mark that draws itself in - used for "username is available".
const DrawnCheck = ({ className = 'w-5 h-5' }) => {
  const reduce = useReducedMotion();
  return (
    <motion.svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <motion.path
        d="M4.5 12.5l4.8 4.8L19.5 7"
        initial={{ pathLength: reduce ? 1 : 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: reduce ? 0 : 0.35, ease: 'easeOut' }}
      />
    </motion.svg>
  );
};

// ── Reusable field ──────────────────────────────────────────────────────────
// The message row is a fixed-height slot so switching between hint / error / the
// password meter never reflows the form (the screen has to stay scroll-free).
function Field({ label, error, touched, hint, hintNode, hintClassName = 'text-gray-400', children, htmlFor }) {
  const show = touched && error;
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-[13px] font-medium text-gray-700">{label}</label>
      {children}
      <div className="mt-0.5 min-h-[16px]">
        <AnimatePresence mode="wait" initial={false}>
          {show ? (
            <motion.p
              key="err"
              initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="text-[11px] leading-4 text-red-600"
            >
              {error}
            </motion.p>
          ) : hintNode ? (
            <motion.div
              key="hint-node"
              initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {hintNode}
            </motion.div>
          ) : hint ? (
            <motion.p
              key={`hint-${hint}`}
              initial={{ opacity: 0, y: -3 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className={`text-[11px] leading-4 ${hintClassName}`}
            >
              {hint}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}

const inputBase =
  'w-full px-3 py-2 tall:py-2.5 rounded-xl border bg-white text-sm text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 focus:ring-4';

function inputCls(invalid) {
  return `${inputBase} ${invalid
    ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
    : 'border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500/10'}`;
}

const ValueProp = ({ title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/15 text-white tall:h-8 tall:w-8">
      <CheckIcon className="w-4 h-4" />
    </div>
    <div>
      <p className="text-sm font-semibold text-white tall:text-base">{title}</p>
      <p className="text-[13px] leading-snug text-white/70">{desc}</p>
    </div>
  </div>
);

// Gently-floating glass chip (decorative) built from an existing brand SVG.
const FloatingChip = ({ src, className = '', delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      aria-hidden
      className={`pointer-events-none absolute z-0 hidden h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur xl:flex ${className}`}
      initial={{ opacity: 0 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: [0, -10, 0] }}
      transition={reduce ? { duration: 0.3 } : {
        opacity: { duration: 0.6, delay },
        y: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay },
      }}
    >
      <img src={src} alt="" className="h-7 w-7 opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </motion.div>
  );
};

const VALUE_PROPS = [
  { title: 'No paperwork upfront', desc: 'Just the basics to get started. Add KYC & bank details only before you ship.' },
  { title: 'One dashboard', desc: 'Orders, tracking, NDR, COD remittance and reports in a single place.' },
  { title: 'Best courier rates', desc: 'Auto-allocation across carriers for the best price and serviceability.' },
];

// Product capabilities (each maps to a real module in the app) - no invented metrics.
const FEATURES = [
  { icon: '/marketplace.svg', label: 'Channel & marketplace orders' },
  { icon: '/bulk_tracking.svg', label: 'Live tracking + NDR' },
  { icon: '/wallet_wayuvega.svg', label: 'Wallet & COD remittance' },
];

const MerchantOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduce = useReducedMotion();

  const [form, setForm] = useState({
    firstName: '', lastName: '', brandName: '',
    username: '', mobile: '', email: '',
    password: '', confirmPassword: '',
  });
  const [touched, setTouched] = useState({});
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // username availability: idle | invalid | checking | available | taken | unknown
  const [usernameState, setUsernameState] = useState({ status: 'idle', message: '' });
  const usernameSeq = useRef(0);

  // Pointer parallax for the hero illustration (desktop panel only).
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const artX = useSpring(pointerX, { stiffness: 50, damping: 18 });
  const artY = useSpring(pointerY, { stiffness: 50, damping: 18 });
  const handlePanelMouseMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    pointerX.set(((e.clientX - r.left) / r.width - 0.5) * 26);
    pointerY.set(((e.clientY - r.top) / r.height - 0.5) * 18);
  };
  const handlePanelMouseLeave = () => { pointerX.set(0); pointerY.set(0); };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const blur = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.brandName.trim()) e.brandName = 'Brand name is required';
    if (!form.username.trim()) e.username = 'Choose a username';
    else if (!USERNAME_REGEX.test(form.username.trim())) e.username = '4–30 chars: letters, numbers, dot or underscore';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!MOBILE_REGEX.test(form.mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile number';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!EMAIL_REGEX.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.password) e.password = 'Create a password';
    else if (form.password.length < 8) e.password = 'Use at least 8 characters';
    if (!form.confirmPassword) e.confirmPassword = 'Re-enter your password';
    else if (form.confirmPassword !== form.password) e.confirmPassword = 'Passwords do not match';
    return e;
  }, [form]);

  const pwScore = scorePassword(form.password);

  // Debounced username availability check.
  useEffect(() => {
    const u = form.username.trim();
    if (!u) { setUsernameState({ status: 'idle', message: '' }); return; }
    if (!USERNAME_REGEX.test(u)) { setUsernameState({ status: 'invalid', message: '' }); return; }

    setUsernameState({ status: 'checking', message: 'Checking availability…' });
    const seq = ++usernameSeq.current;
    const t = setTimeout(async () => {
      const { available } = await checkUsernameAvailable(u);
      if (seq !== usernameSeq.current) return; // stale
      if (available === true) setUsernameState({ status: 'available', message: 'Username is available' });
      else if (available === false) setUsernameState({ status: 'taken', message: 'That username is already taken' });
      else setUsernameState({ status: 'unknown', message: "Couldn't verify right now - you can still continue" });
    }, 500);
    return () => clearTimeout(t);
  }, [form.username]);

  const canSubmit =
    Object.keys(errors).length === 0 &&
    agree &&
    usernameState.status !== 'taken' &&
    usernameState.status !== 'checking' &&
    !submitting;

  const handleSubmit = useCallback(async (evt) => {
    evt?.preventDefault?.();
    setTouched({ firstName: true, lastName: true, brandName: true, username: true, mobile: true, email: true, password: true, confirmPassword: true });
    if (Object.keys(errors).length > 0 || !agree) return;
    if (usernameState.status === 'taken' || usernameState.status === 'checking') return;

    setSubmitting(true);
    setError(null);
    try {
      const brand = form.brandName.trim();
      const reg = await registerUser({
        organization_name: brand,
        brand_name: brand,
        platform_id: 1,
        is_standard_user: true,
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        role_id: 1,
        firstname: form.firstName.trim(),
        lastname: form.lastName.trim(),
      });
      if (!reg?.success) {
        throw new Error(reg?.error || 'We could not create your account. Please try again.');
      }

      let token = await authenticateUser({ username: form.username.trim(), password: form.password });
      let decoded = jwtDecode(token);
      const clientId = decoded.clientId || decoded.parentClientId;
      let merchantId = decoded.merchantId;

      // Create the POCAS onboarding record if the token doesn't already carry a merchantId.
      // Best-effort: never block account creation on a downstream hiccup.
      if (!merchantId) {
        try {
          const initiated = await initiateOnboarding(clientId, token);
          merchantId = initiated?.merchant_id || initiated?.merchantId;
          const fresh = await authenticateUser({ username: form.username.trim(), password: form.password });
          token = fresh;
          decoded = jwtDecode(fresh);
          merchantId = decoded.merchantId || merchantId;
        } catch (initErr) {
          console.warn('initiate onboarding failed (continuing):', initErr?.message);
          merchantId = merchantId || clientId;
        }
      }

      // Start the new account on a clean slate - clear any cached data left by a previous session
      // on this browser before persisting the new one (branding is preserved).
      clearClientCache();

      // Persist session for the rest of the app (redux + localStorage fallbacks used by the hub/guard).
      try {
        localStorage.setItem('authToken', token);
        if (clientId) localStorage.setItem('parentClientId', clientId);
        if (merchantId) localStorage.setItem('merchantId', merchantId);
      } catch (_) { /* storage may be unavailable */ }
      dispatch(restoreSession({ token, user: decoded }));

      // Seed the minimal ACCOUNT-phase details so the Hub shows progress. Best-effort only.
      try {
        await updateOnboardingDetails({
          parent_client_id: clientId,
          merchant_id: merchantId,
          basic_details: {
            email: form.email.trim(),
            phone: form.mobile.trim(),
            name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
            brand_name: brand,
            email_verified: false,
            phone_verified: false,
          },
          // Required by POCAS on update (drives wallet creation). Default to PREPAID at signup;
          // the merchant can change wallet preference later during progressive onboarding.
          wallet_type_preference: 'PREPAID',
        }, token);
      } catch (updErr) {
        console.warn('seeding basic details failed (continuing):', updErr?.message);
      }

      // Non-critical signup tracking.
      try {
        await saveMerchantSignupToSheet({
          merchantId, username: form.username.trim(), password: form.password,
          parentClientId: clientId, email: form.email.trim(), phone: form.mobile.trim(),
          name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(), brandName: brand,
        });
      } catch (_) { /* optional */ }

      // Land the merchant in the guided onboarding wizard with a celebratory intro. All the
      // account-creation API calls above are unchanged; the wizard picks up progressively from here.
      navigate('/onboarding', { replace: true, state: { justSignedUp: true } });
    } catch (err) {
      console.error('Signup failed:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }, [errors, agree, usernameState.status, form, dispatch, navigate]);

  const usernameHintClass =
    usernameState.status === 'available' ? 'text-green-600'
    : usernameState.status === 'taken' ? 'text-red-600'
    : usernameState.status === 'unknown' ? 'text-amber-600'
    : 'text-gray-400';

  const usernameHint =
    usernameState.status === 'checking' ? 'Checking availability…'
    : usernameState.status === 'available' ? 'Username is available'
    : usernameState.status === 'taken' ? 'That username is already taken'
    : usernameState.status === 'unknown' ? "Couldn't verify right now - you can still continue"
    : 'You’ll use this to log in';

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 lg:h-screen lg:overflow-hidden">
      <div className="grid min-h-screen grid-cols-1 lg:h-full lg:min-h-0 lg:grid-cols-[1.05fr_1fr]">
        {/* Left - brand / value panel (full-bleed split screen) */}
        <div
          className="relative hidden justify-center gap-6 overflow-hidden bg-gradient-to-br from-indigo-700 via-blue-700 to-violet-800 p-8 tall:justify-between tall:gap-0 tall:p-10 taller:p-14 lg:flex lg:h-full lg:flex-col"
          onMouseMove={handlePanelMouseMove}
          onMouseLeave={handlePanelMouseLeave}
        >
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl"
            animate={reduce ? undefined : { scale: [1, 1.15, 1] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-fuchsia-400/20 blur-3xl"
            animate={reduce ? undefined : { scale: [1.1, 1, 1.1] }} transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute right-1/3 top-1/2 h-56 w-56 rounded-full bg-cyan-300/15 blur-3xl"
            animate={reduce ? undefined : { scale: [1, 1.2, 1], x: [0, 24, 0] }} transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg,#fff 1px, transparent 1px)', backgroundSize: '42px 42px' }}
            aria-hidden
          />
          <FloatingChip src="/bulk_shipments.svg" className="left-8 top-[38%]" delay={0} />
          <FloatingChip src="/reports.svg" className="right-10 top-24" delay={0.6} />
          <FloatingChip src="/precision.svg" className="bottom-40 right-12" delay={1.2} />

          <div className="relative z-10 mx-auto flex w-full max-w-xl items-center gap-3">
            <img src={LOGO_WHITE} alt="Wayuvega" className="h-8 w-auto tall:h-9" />
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white/90">for business</span>
          </div>

          <div className="relative z-10 mx-auto w-full max-w-xl space-y-4 tall:space-y-6">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="text-3xl font-extrabold leading-[1.1] text-white tall:text-4xl taller:text-[42px]"
              >
                Ship smarter.<br />Grow faster.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-2 max-w-md text-sm text-white/80 tall:mt-3 tall:text-base taller:text-lg"
              >
                Create your account now and finish KYC later - explore the dashboard right away.
              </motion.p>
            </div>

            {/* Hero illustration: pointer parallax on the wrapper, idle float on the art */}
            <motion.div
              aria-hidden
              className="pointer-events-none relative max-w-lg"
              style={{ x: artX, y: artY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            >
              <div className="absolute inset-x-6 bottom-2 h-10 rounded-full bg-indigo-950/30 blur-2xl" />
              <motion.img
                src={HERO_ART}
                alt=""
                className="relative max-h-[20vh] w-full object-contain drop-shadow-2xl tall:max-h-[26vh] taller:max-h-[300px]"
                animate={reduce ? undefined : { y: [0, -12, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </motion.div>

            <div className="space-y-2.5 tall:space-y-4">
              {VALUE_PROPS.map((vp, i) => (
                <motion.div key={vp.title} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.1, duration: 0.4 }}>
                  <ValueProp title={vp.title} desc={vp.desc} />
                </motion.div>
              ))}
            </div>

            <div className="hidden max-w-lg flex-wrap gap-2 tall:flex">
              {FEATURES.map((f, i) => (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 + i * 0.1 }}
                  className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 backdrop-blur"
                >
                  <img src={f.icon} alt="" aria-hidden className="h-4 w-4 opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <span className="text-xs font-medium text-white/85">{f.label}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="relative z-10 mx-auto hidden w-full max-w-xl items-center gap-2 text-xs text-white/60 tall:flex tall:text-sm">
            <img src="/lock.svg" alt="" aria-hidden className="h-4 w-4 opacity-70" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            Nothing is charged today - add KYC &amp; bank details only when you’re ready to ship.
          </div>
        </div>

        {/* Right - form. Height-bound on desktop: my-auto centres it when it fits and the
            column scrolls internally (never the page) on extremely short windows. */}
        <div className="flex justify-center px-5 py-6 sm:px-8 tall:py-10 lg:h-full lg:overflow-y-auto lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="my-auto w-full max-w-lg"
          >
            {/* Compact brand hero for small screens (the left panel is desktop-only) */}
            <div className="relative mb-5 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-700 via-blue-700 to-violet-800 px-5 py-4 lg:hidden">
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/15 blur-2xl"
                animate={reduce ? undefined : { scale: [1, 1.2, 1] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
              />
              <div className="relative flex items-center justify-between gap-3">
                <div>
                  <img src={LOGO_WHITE} alt="Wayuvega" className="h-7 w-auto" />
                  <p className="mt-2 text-sm font-semibold text-white">Ship smarter. Grow faster.</p>
                  <p className="text-xs text-white/70">Set up in under a minute.</p>
                </div>
                <motion.img
                  src="/person_with_box.png"
                  alt=""
                  aria-hidden
                  className="h-20 w-auto flex-shrink-0 drop-shadow-xl"
                  animate={reduce ? undefined : { y: [0, -6, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="mb-3 flex items-center justify-end">
              <span className="text-[13px] text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-blue-600 hover:underline">Log in</Link>
              </span>
            </div>

            <h2 className="text-lg font-bold text-gray-900 tall:text-2xl">Create your account</h2>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-700"
                >
                  <XIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <form className="mt-3.5 space-y-2" onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
                <Field label="First name" htmlFor="firstName" error={errors.firstName} touched={touched.firstName}>
                  <input id="firstName" type="text" autoComplete="given-name" value={form.firstName}
                    onChange={set('firstName')} onBlur={blur('firstName')} className={inputCls(touched.firstName && errors.firstName)} placeholder="Aditi" />
                </Field>
                <Field label="Last name" htmlFor="lastName" error={errors.lastName} touched={touched.lastName}>
                  <input id="lastName" type="text" autoComplete="family-name" value={form.lastName}
                    onChange={set('lastName')} onBlur={blur('lastName')} className={inputCls(touched.lastName && errors.lastName)} placeholder="Sharma" />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
                <Field label="Mobile" htmlFor="mobile" error={errors.mobile} touched={touched.mobile}>
                  <input id="mobile" type="tel" inputMode="numeric" maxLength={10} autoComplete="tel" value={form.mobile}
                    onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value.replace(/\D/g, '') }))} onBlur={blur('mobile')}
                    className={inputCls(touched.mobile && errors.mobile)} placeholder="9876543210" />
                </Field>
                <Field label="Email" htmlFor="email" error={errors.email} touched={touched.email}>
                  <input id="email" type="email" autoComplete="email" value={form.email}
                    onChange={set('email')} onBlur={blur('email')} className={inputCls(touched.email && errors.email)} placeholder="you@brand.com" />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
              <Field label="Brand / business name" htmlFor="brandName" error={errors.brandName} touched={touched.brandName}>
                <input id="brandName" type="text" autoComplete="organization" value={form.brandName}
                  onChange={set('brandName')} onBlur={blur('brandName')} className={inputCls(touched.brandName && errors.brandName)} placeholder="Acme Retail" />
              </Field>

              {/* Username with live availability */}
              <Field
                label="Username" htmlFor="username"
                error={errors.username} touched={touched.username}
                hintClassName={usernameHintClass}
                hint={usernameHint}
              >
                <div className="relative">
                  <input
                    id="username" type="text" autoComplete="username" value={form.username}
                    onChange={set('username')} onBlur={blur('username')}
                    className={`${inputCls((touched.username && errors.username) || usernameState.status === 'taken')} pr-10`}
                    placeholder="acme_retail"
                  />
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    <AnimatePresence mode="wait" initial={false}>
                      {usernameState.status === 'checking' && (
                        <motion.span key="checking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <Spinner className="h-4 w-4 text-gray-400" />
                        </motion.span>
                      )}
                      {usernameState.status === 'available' && (
                        <motion.span
                          key="available"
                          initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                          className="text-green-600"
                        >
                          <DrawnCheck className="h-5 w-5" />
                        </motion.span>
                      )}
                      {usernameState.status === 'taken' && (
                        <motion.span
                          key="taken"
                          initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: reduce ? 0 : [0, -3, 3, 0] }} exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="text-red-500"
                        >
                          <XIcon className="h-5 w-5" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </Field>
              </div>

              <div className="grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
              {/* Password. The strength meter lives in the fixed-height message slot so
                  typing never grows the form. */}
              <Field
                label="Password" htmlFor="password" error={errors.password} touched={touched.password}
                hint="At least 8 characters"
                hintNode={form.password ? (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 gap-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-gray-200">
                          <motion.div
                            className={`h-full rounded-full ${STRENGTH[pwScore].color}`}
                            initial={false}
                            animate={{ scaleX: i < pwScore ? 1 : 0 }}
                            style={{ originX: 0 }}
                            transition={{ duration: reduce ? 0 : 0.3, ease: 'easeOut' }}
                          />
                        </div>
                      ))}
                    </div>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={pwScore}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className={`text-[11px] font-medium leading-4 ${STRENGTH[pwScore].text}`}
                      >
                        {STRENGTH[pwScore].label}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                ) : null}
              >
                <div className="relative">
                  <input id="password" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={form.password}
                    onChange={set('password')} onBlur={blur('password')} className={`${inputCls(touched.password && errors.password)} pr-14`} placeholder="8+ characters" />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                    className="absolute inset-y-0 right-3 my-auto text-[11px] font-semibold text-gray-500 hover:text-gray-700">
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>

              <Field label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword} touched={touched.confirmPassword}>
                <div className="relative">
                  <input id="confirmPassword" type={showPw ? 'text' : 'password'} autoComplete="new-password" value={form.confirmPassword}
                    onChange={set('confirmPassword')} onBlur={blur('confirmPassword')} className={`${inputCls(touched.confirmPassword && errors.confirmPassword)} pr-10`} placeholder="Re-enter password" />
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    <AnimatePresence initial={false}>
                      {form.confirmPassword && form.confirmPassword === form.password && (
                        <motion.span
                          key="match"
                          initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                          className="text-green-600"
                        >
                          <DrawnCheck className="h-5 w-5" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </Field>
              </div>

              <label className="mt-1 flex cursor-pointer items-start gap-2.5 text-[13px] text-gray-600">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-and-conditions" className="font-medium text-blue-600 hover:underline">Terms</Link> and{' '}
                  <Link to="/privacy-policy" className="font-medium text-blue-600 hover:underline">Privacy Policy</Link>.
                </span>
              </label>

              <motion.button
                type="submit" disabled={!canSubmit}
                whileHover={canSubmit ? { scale: 1.01 } : undefined}
                whileTap={canSubmit ? { scale: 0.99 } : undefined}
                className={`relative mt-3 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all tall:mt-4 tall:py-3 ${
                  canSubmit ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:shadow-blue-500/30' : 'cursor-not-allowed bg-gray-300'
                }`}
              >
                {canSubmit && !reduce && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/25 blur-md"
                    animate={{ x: ['0%', '420%'] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.1 }}
                  />
                )}
                <span className="relative flex items-center gap-2">
                  {submitting ? (<><Spinner className="h-4 w-4" /> Creating your account…</>) : (<>Create account &amp; continue →</>)}
                </span>
              </motion.button>

              <p className="mt-3 hidden text-center text-[11px] leading-4 text-gray-400 tall:block">
                You can explore the app right away. KYC &amp; bank details are needed only before you ship.
              </p>
            </form>

            {/* Brand sign-off on small screens where the desktop panel is hidden */}
            <div className="mt-5 flex items-center justify-center gap-2 lg:hidden">
              <img src={LOGO_DARK} alt="Wayuvega" className="h-5 w-auto opacity-50" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MerchantOnboarding;
